/**
 * RowActions — the shared Actions column for admin tables (ORCH-298).
 *
 * Every admin table renders its row actions through this component so the
 * order (Edit → module-specific actions → Disable/Deactivate → Delete), the
 * wording and the icons cannot drift per module. Callers decide which actions
 * a row offers and when they are disabled; this component decides how they look.
 */
import React from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import {
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  PersonOff as PersonOffIcon,
} from '@mui/icons-material';

/** 'deactivate' is reserved for person/account entities (users); everything else is 'disable'. */
export type DeactivationVariant = 'disable' | 'deactivate';

const DEACTIVATION_COPY: Record<
  DeactivationVariant,
  { off: string; on: string; offIcon: React.ReactElement }
> = {
  disable: { off: 'Disable', on: 'Activate', offIcon: <BlockIcon fontSize="small" /> },
  deactivate: { off: 'Deactivate', on: 'Reactivate', offIcon: <PersonOffIcon fontSize="small" /> },
};

export interface RowActionButtonProps {
  /** Visible tooltip text, e.g. "Revert to Global Field". */
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  testId?: string;
  ariaLabel?: string;
  disabled?: boolean;
  /** Tooltip shown instead of `label` while disabled. */
  disabledReason?: string;
}

/**
 * One icon button in an actions row. Exported so module-specific actions
 * (Manage Alternate Names, Revert to Global, Duplicate…) render the same way
 * as the standard ones.
 */
export const RowActionButton: React.FC<RowActionButtonProps> = ({
  label,
  icon,
  onClick,
  testId,
  ariaLabel,
  disabled = false,
  disabledReason,
}) => {
  const button = (
    <IconButton
      size="small"
      aria-label={ariaLabel ?? label}
      data-testid={testId}
      disabled={disabled}
      onClick={(event) => {
        // Rows open the editor on click; an action must not also do that
        event.stopPropagation();
        onClick();
      }}
    >
      {icon}
    </IconButton>
  );
  return (
    <Tooltip title={disabled && disabledReason ? disabledReason : label}>
      {/* A disabled button fires no events, so a span has to carry its tooltip */}
      {disabled ? <span>{button}</span> : button}
    </Tooltip>
  );
};

export interface RowActionsProps {
  /** Title Case singular noun for tooltips, e.g. "Tenant", "Field Definition". */
  entityLabel: string;
  /** Row name appended to aria-labels so screen readers can tell rows apart. */
  rowLabel?: string;
  /** Prefix for the generated test ids: `${prefix}-edit`, `${prefix}-disable`, … */
  testIdPrefix: string;
  /** data-testid on the wrapping row container. */
  rowTestId?: string;

  /** Omit to hide Edit (e.g. the user may not edit this row). */
  onEdit?: () => void;
  editDisabled?: boolean;

  /** Omit for entities with no soft-deactivate action. */
  deactivation?: {
    variant: DeactivationVariant;
    /** true renders the switch-off action; false renders the switch-back-on action. */
    isActive: boolean;
    onToggle: () => void;
    disabled?: boolean;
  };

  /** Omit for entities with no delete action. */
  onDelete?: () => void;
  deleteDisabled?: boolean;
  deleteDisabledReason?: string;
  /** 'Remove' is for taking something out of a container without destroying it (a user's tenant membership). */
  deleteVerb?: 'Delete' | 'Remove';

  /** Module-specific actions, rendered between Edit and Disable/Delete. Use RowActionButton. */
  secondaryActions?: React.ReactNode;
}

const RowActions: React.FC<RowActionsProps> = ({
  entityLabel,
  rowLabel,
  testIdPrefix,
  rowTestId,
  onEdit,
  editDisabled,
  deactivation,
  onDelete,
  deleteDisabled,
  deleteDisabledReason,
  deleteVerb = 'Delete',
  secondaryActions,
}) => {
  const aria = (label: string) => (rowLabel ? `${label} ${rowLabel}` : label);

  const deactivationButton = (() => {
    if (!deactivation) return null;
    const copy = DEACTIVATION_COPY[deactivation.variant];
    const verb = deactivation.isActive ? copy.off : copy.on;
    const label = `${verb} ${entityLabel}`;
    return (
      <RowActionButton
        label={label}
        ariaLabel={aria(label)}
        testId={`${testIdPrefix}-${verb.toLowerCase()}`}
        icon={
          deactivation.isActive ? (
            copy.offIcon
          ) : (
            <CheckCircleIcon fontSize="small" color="success" />
          )
        }
        onClick={deactivation.onToggle}
        disabled={deactivation.disabled}
      />
    );
  })();

  const editLabel = `Edit ${entityLabel}`;
  const deleteLabel = `${deleteVerb} ${entityLabel}`;

  return (
    <Box sx={{ display: 'flex', gap: 0.5 }} data-testid={rowTestId}>
      {onEdit && (
        <RowActionButton
          label={editLabel}
          ariaLabel={aria(editLabel)}
          testId={`${testIdPrefix}-edit`}
          icon={<EditIcon fontSize="small" />}
          onClick={onEdit}
          disabled={editDisabled}
        />
      )}
      {secondaryActions}
      {deactivationButton}
      {onDelete && (
        <RowActionButton
          label={deleteLabel}
          ariaLabel={aria(deleteLabel)}
          testId={`${testIdPrefix}-${deleteVerb.toLowerCase()}`}
          icon={<DeleteIcon fontSize="small" />}
          onClick={onDelete}
          disabled={deleteDisabled}
          disabledReason={deleteDisabledReason}
        />
      )}
    </Box>
  );
};

export default RowActions;
